// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Byte sources — the abstraction that lets one parser read a remote GGUF over
 * HTTP Range and a local one via `Blob.slice`, with no branching in between.
 *
 * The remote source is the whole trick of this tool: `Range` is a
 * CORS-safelisted request header, so a single-range `bytes=0-N` request fires
 * no preflight, and Hugging Face's CDN answers `206` with
 * `access-control-allow-origin: *` and `access-control-expose-headers: *` —
 * which means the browser can read `content-range` and learn the true size of
 * a file it has not downloaded.
 */

import { NotGguf } from './types';

export interface ByteSource {
  /** Human label for the event log. */
  readonly label: string;
  /** Total file size in bytes, once known. */
  readonly totalBytes: number | null;
  /** Read bytes [0, end). May return more than asked for; never fewer unless EOF. */
  read(end: number, signal?: AbortSignal): Promise<Uint8Array>;
}

/** How the progressive read grows. Measured against real models: the tensor
 *  table of a Qwen3-8B ends 5.68 MB in, a Llama-3.2-1B-Q8_0 7.47 MB in — the
 *  tokenizer's token and merges arrays sit in between. 8 MB covers the common
 *  case in a single request; the ladder is for unusually large vocabularies. */
export const READ_LADDER = [8 << 20, 24 << 20, 64 << 20, 160 << 20];

export class LocalFileSource implements ByteSource {
  readonly label: string;
  readonly totalBytes: number;
  private cache: Uint8Array | null = null;

  constructor(private readonly file: File) {
    this.label = file.name;
    this.totalBytes = file.size;
  }

  async read(end: number): Promise<Uint8Array> {
    const want = Math.min(end, this.totalBytes);
    if (this.cache && this.cache.byteLength >= want) return this.cache;
    const buf = await this.file.slice(0, want).arrayBuffer();
    this.cache = new Uint8Array(buf);
    return this.cache;
  }
}

export class RemoteSource implements ByteSource {
  readonly label: string;
  totalBytes: number | null = null;
  /** True once a 206 has confirmed the server honours Range requests. */
  rangeSupported = false;
  private cache: Uint8Array | null = null;

  constructor(
    readonly url: string,
    label?: string,
  ) {
    this.label = label ?? url;
  }

  async read(end: number, signal?: AbortSignal): Promise<Uint8Array> {
    if (this.cache && this.cache.byteLength >= end) return this.cache;
    if (this.totalBytes !== null && this.cache && this.cache.byteLength >= this.totalBytes) {
      return this.cache; // already have the whole (small) file
    }

    let res: Response;
    try {
      res = await fetch(this.url, {
        headers: { Range: `bytes=0-${end - 1}` },
        signal,
        // Referrer is not needed and is one less thing to leak.
        referrerPolicy: 'no-referrer',
        credentials: 'omit',
      });
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') throw err;
      // A cross-origin fetch that fails before producing a response is almost
      // always a missing Access-Control-Allow-Origin — the host serves the file
      // fine, it just doesn't let a browser read it. GitHub release assets and
      // ollama.com both behave this way. Saying "network error" here would send
      // the user looking for a problem on their end.
      throw new NotGguf(
        `${new URL(this.url).hostname} doesn't allow browsers to read its files directly (no CORS headers), so this page can't fetch it. Hugging Face does allow it — or download the file and drop it in below, which works offline and uploads nothing.`,
      );
    }

    if (res.status === 401 || res.status === 403) {
      res.body?.cancel();
      throw new NotGguf(
        'This repository is gated — Hugging Face requires you to accept its licence while signed in, so an anonymous request is refused. Many gated models have byte-identical ungated mirrors (for example an "unsloth/" or "bartowski/" copy); try one of those, or download the file and drop it in below.',
      );
    }
    if (res.status === 404) {
      res.body?.cancel();
      throw new NotGguf('No file at that address (404). Check the repo id, branch and filename.');
    }
    if (!res.ok) {
      res.body?.cancel();
      throw new NotGguf(`The server answered ${res.status} ${res.statusText || ''}`.trim());
    }

    if (res.status === 206) {
      this.rangeSupported = true;
      const cr = res.headers.get('content-range');
      const total = cr ? Number(cr.split('/')[1]) : NaN;
      if (Number.isFinite(total) && total > 0) this.totalBytes = total;
      const bytes = new Uint8Array(await res.arrayBuffer());
      this.cache = bytes;
      return bytes;
    }

    // Status 200: the server ignored the Range header and is about to hand us
    // the entire file. On a 20 GB model that is exactly the download this tool
    // exists to avoid, so take only what we asked for and cancel the rest.
    const len = Number(res.headers.get('content-length'));
    if (Number.isFinite(len) && len > 0) this.totalBytes = len;
    const bytes = await readAtMost(res, end);
    this.cache = bytes;
    return bytes;
  }
}

/** Drain at most `limit` bytes from a response, then cancel the body. */
async function readAtMost(res: Response, limit: number): Promise<Uint8Array> {
  if (!res.body) return new Uint8Array(await res.arrayBuffer()).slice(0, limit);
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let got = 0;
  try {
    while (got < limit) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      got += value.byteLength;
    }
  } finally {
    // Stop the transfer; without this the browser keeps pulling the whole file.
    await reader.cancel().catch(() => undefined);
  }
  const out = new Uint8Array(Math.min(got, limit));
  let p = 0;
  for (const ch of chunks) {
    if (p >= out.byteLength) break;
    const take = Math.min(ch.byteLength, out.byteLength - p);
    out.set(ch.subarray(0, take), p);
    p += take;
  }
  return out;
}

// ── Address parsing ──────────────────────────────────────────────────

export interface ResolvedTarget {
  kind: 'file' | 'repo';
  /** For kind 'file': the full resolve URL to Range-read. */
  url?: string;
  /** For both: the HF repo id, when the input named one. */
  repo?: string;
  /** For kind 'file': the path within the repo, when known. */
  path?: string;
  revision?: string;
  /** A short label for the UI. */
  label: string;
}

const HF_HOSTS = new Set(['huggingface.co', 'www.huggingface.co', 'hf.co']);

/**
 * Turn whatever the user pasted into either a direct file URL or a repo to list.
 *
 * Accepts:
 *   Qwen/Qwen3-8B-GGUF
 *   hf.co/Qwen/Qwen3-8B-GGUF
 *   https://huggingface.co/Qwen/Qwen3-8B-GGUF
 *   https://huggingface.co/Qwen/Qwen3-8B-GGUF/blob/main/Qwen3-8B-Q4_K_M.gguf
 *   https://huggingface.co/Qwen/Qwen3-8B-GGUF/resolve/main/Qwen3-8B-Q4_K_M.gguf
 *   Qwen/Qwen3-8B-GGUF:Q4_K_M            (the llama.cpp -hf shorthand)
 *   https://example.com/anything.gguf    (any direct URL)
 */
export function parseTarget(inputRaw: string): ResolvedTarget {
  const input = inputRaw.trim();
  if (!input) throw new NotGguf('Paste a Hugging Face repo id, a .gguf URL, or drop a local file.');

  // The llama.cpp `-hf user/repo:QUANT` shorthand.
  const shorthand = /^([^/\s:]+\/[^/\s:]+):([A-Za-z0-9_.-]+)$/.exec(input);
  if (shorthand) {
    return {
      kind: 'repo',
      repo: shorthand[1],
      label: shorthand[1],
      revision: 'main',
      path: shorthand[2], // used as a quant filter hint
    };
  }

  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(input) || input.startsWith('hf.co/')) {
    const url = new URL(input.startsWith('hf.co/') ? `https://${input}` : input);
    if (HF_HOSTS.has(url.hostname)) {
      const seg = url.pathname.replace(/^\/+/, '').split('/').filter(Boolean);
      const marker = seg.findIndex((s) => s === 'resolve' || s === 'blob' || s === 'tree');
      if (marker >= 2) {
        const repo = seg.slice(0, marker).join('/');
        const revision = seg[marker + 1] ?? 'main';
        const path = seg.slice(marker + 2).join('/');
        if (path) {
          return {
            kind: 'file',
            url: hfResolveUrl(repo, revision, path),
            repo,
            path,
            revision,
            label: path.split('/').pop()!,
          };
        }
        return { kind: 'repo', repo, revision, label: repo };
      }
      if (seg.length >= 2) return { kind: 'repo', repo: seg.slice(0, 2).join('/'), revision: 'main', label: seg.slice(0, 2).join('/') };
      throw new NotGguf('That looks like a Hugging Face link, but not to a model repo.');
    }
    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      throw new NotGguf('Only http(s) addresses can be read.');
    }
    return { kind: 'file', url: url.toString(), label: decodeURIComponent(url.pathname.split('/').pop() || url.hostname) };
  }

  // Bare `owner/name`, optionally with a path after it.
  const seg = input.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  if (seg.length === 2) return { kind: 'repo', repo: seg.join('/'), revision: 'main', label: seg.join('/') };
  if (seg.length > 2 && input.toLowerCase().endsWith('.gguf')) {
    const repo = seg.slice(0, 2).join('/');
    const path = seg.slice(2).join('/');
    return { kind: 'file', url: hfResolveUrl(repo, 'main', path), repo, path, revision: 'main', label: seg[seg.length - 1] };
  }

  throw new NotGguf(
    'Could not read that address. Try a repo id like "Qwen/Qwen3-8B-GGUF", or a full .gguf URL.',
  );
}

export function hfResolveUrl(repo: string, revision: string, path: string): string {
  const rev = encodeURIComponent(revision || 'main');
  const p = path.split('/').map(encodeURIComponent).join('/');
  return `https://huggingface.co/${repo}/resolve/${rev}/${p}`;
}

export interface RepoFile {
  path: string;
  size: number;
  /** Set when the file is one shard of a split model. */
  shard?: { index: number; of: number; base: string };
}

/** `model-00001-of-00003.gguf` → the shard triple. */
export function parseShard(path: string): RepoFile['shard'] {
  const m = /^(.*)-(\d{5})-of-(\d{5})\.gguf$/i.exec(path);
  if (!m) return undefined;
  return { base: m[1], index: Number(m[2]), of: Number(m[3]) };
}

/**
 * List the .gguf files in a repo through the public HF tree API.
 *
 * This is an anonymous read of public repository metadata — the same JSON the
 * model page itself renders from. It returns the LFS byte size, which is how
 * we show file sizes before touching a single model byte.
 */
export async function listRepoGgufs(
  repo: string,
  revision = 'main',
  signal?: AbortSignal,
): Promise<RepoFile[]> {
  const out: RepoFile[] = [];
  // The tree API paginates via a Link header; GGUF repos are small, but a few
  // (bartowski's, notably) carry 30+ quants across several pages.
  let url: string | null = `https://huggingface.co/api/models/${repo}/tree/${encodeURIComponent(revision)}?recursive=true`;
  for (let page = 0; url && page < 10; page++) {
    const res: Response = await fetch(url, { signal, credentials: 'omit', referrerPolicy: 'no-referrer' });
    if (res.status === 401 || res.status === 403) {
      throw new NotGguf(
        `"${repo}" is gated or private — Hugging Face refuses anonymous listing. Try an ungated mirror, or drop the file in directly.`,
      );
    }
    if (res.status === 404) throw new NotGguf(`No such repository: "${repo}".`);
    if (!res.ok) throw new NotGguf(`Hugging Face answered ${res.status} while listing "${repo}".`);

    const items = (await res.json()) as Array<{ type: string; path: string; size?: number; lfs?: { size?: number } }>;
    for (const it of items) {
      if (it.type !== 'file' || !it.path.toLowerCase().endsWith('.gguf')) continue;
      out.push({ path: it.path, size: it.lfs?.size ?? it.size ?? 0, shard: parseShard(it.path) });
    }
    url = nextLink(res.headers.get('link'));
  }
  if (!out.length) throw new NotGguf(`"${repo}" has no .gguf files. GGUFScope reads GGUF only.`);
  out.sort((a, b) => a.path.localeCompare(b.path, 'en', { numeric: true }));
  return out;
}

function nextLink(header: string | null): string | null {
  if (!header) return null;
  for (const part of header.split(',')) {
    const m = /<([^>]+)>\s*;\s*rel="?next"?/.exec(part);
    if (m) return m[1];
  }
  return null;
}

/**
 * Group shards so a split model is offered as one entry with its true total
 * size. Only the first shard carries the full metadata, so that is the one we
 * read; the others contribute bytes only.
 */
export function groupShards(files: RepoFile[]): Array<RepoFile & { totalSize: number; shardCount: number }> {
  const bases = new Map<string, RepoFile[]>();
  const singles: RepoFile[] = [];
  for (const f of files) {
    if (f.shard) {
      const list = bases.get(f.shard.base) ?? [];
      list.push(f);
      bases.set(f.shard.base, list);
    } else singles.push(f);
  }
  const out = singles.map((f) => ({ ...f, totalSize: f.size, shardCount: 1 }));
  for (const [, list] of bases) {
    list.sort((a, b) => (a.shard!.index - b.shard!.index));
    const first = list[0];
    out.push({
      ...first,
      totalSize: list.reduce((s, f) => s + f.size, 0),
      shardCount: list[0].shard!.of,
    });
  }
  out.sort((a, b) => a.path.localeCompare(b.path, 'en', { numeric: true }));
  return out;
}
